import { Component, EventEmitter, Input, Output } from '@angular/core';

export type ArtworkCardSize = 'sm' | 'md' | 'lg';
export type ArtworkStatusTone = 'available' | 'sold' | 'consigned' | 'pending' | 'declined' | 'neutral';

/**
 * Design-system artwork card — the single, consistent way to present an artwork.
 *
 * ALWAYS a square cell with `object-fit: contain` (the never-crop hard rule is satisfied
 * by construction at every size). Below the image, an opt-in meta block (name/artist/
 * subtitle/price/status). One layout, configured by inputs — there is no separate "row"
 * style. Per-card buttons go in the `[actions]` content slot. Pickers use `selectable`.
 *
 * Data-shape agnostic: callers map their DTO (ArtistProduct, GalleryConsignmentProduct,
 * NewProduct, …) onto the plain inputs, so one card serves every screen.
 */
@Component({
    selector: 'ds-artwork-card',
    templateUrl: './ds-artwork-card.component.html',
    styleUrls: ['./ds-artwork-card.component.css'],
    standalone: false
})
export class DsArtworkCardComponent {
    @Input() imageUrl?: string | null;
    @Input() name?: string | null;
    @Input() artist?: string | null;
    @Input() subtitle?: string | null;        // any secondary line (art type, date, …)
    @Input() price?: number | null;
    @Input() priceLabel?: string | null;       // e.g. 'Artist revenue'
    @Input() currency: string = 'USD';
    @Input() status?: string | null;           // badge label (Available / Sold / …)
    @Input() statusTone: ArtworkStatusTone = 'neutral';
    @Input() size: ArtworkCardSize = 'md';
    @Input() selectable = false;
    @Input() selected = false;
    @Input() clickable = true;
    @Input() fallback = 'assets/images/No-Image.png';

    @Output() cardClick = new EventEmitter<void>();
    @Output() selectedChange = new EventEmitter<boolean>();

    public onClick(): void {
        if (this.selectable) { this.toggle(); return; }
        if (this.clickable) { this.cardClick.emit(); }
    }

    public toggle(): void {
        this.selected = !this.selected;
        this.selectedChange.emit(this.selected);
    }

    public onImgError(e: Event): void {
        const img = e.target as HTMLImageElement;
        if (img.src.indexOf(this.fallback) === -1) { img.src = this.fallback; }
    }
}
